// CRM-iframe inbound reporter.
//
// telegram-tt's live MTProto session already receives every DM in real time,
// so instead of the CRM worker opening a SECOND session to poll getDifference,
// we forward the events the iframe already has to the parent SPA, which calls
// the backend (ensure-lead + fill-once "ведущий" account, refresh self-name).
// This is a best-effort FAST PATH — the worker's account-health sweep stays
// the authoritative backstop, so anything missed here is still picked up later.
//
// Lives in its own module (not crmBridge.ts) because crmBridge is imported by
// the gramjs worker too, where there is no global store / window. This file is
// imported only from index.tsx on the main thread.

import { addActionHandler, getGlobal } from '../global';
import { getMainUsername } from '../global/helpers/users';

import { getCrmAccountId, getCrmOrigin, isCrmIframeMode } from './crmBridge';
import { isUserId } from './entities/ids';
import { getServerTime } from './serverTime';

let isRegistered = false;
let reporterStartedAt = 0;
let isSelfReported = false;

function postToCrm(payload: object) {
  const origin = getCrmOrigin();
  if (!origin || !window.parent || window.parent === window) return;
  try {
    window.parent.postMessage(payload, origin);
  } catch {
    // Best effort — never block the update pipeline on telemetry.
  }
}

// telegram-tt is logged in AS this account, so its own profile is already in
// global — report it once (no extra getMe) so the CRM reflects a username
// added/renamed after connect instantly. The worker sweep is the fallback.
function maybeReportSelfProfile() {
  if (isSelfReported) return;
  const global = getGlobal();
  const selfId = global.currentUserId;
  if (!selfId) return;
  const me = global.users.byId[selfId];
  if (!me) return;
  isSelfReported = true;
  postToCrm({
    type: 'tg:selfProfile',
    payload: {
      accountId: getCrmAccountId(),
      username: getMainUsername(me),
      firstName: me.firstName,
      lastName: me.lastName,
    },
  });
}

export function registerCrmInboundReporter() {
  if (isRegistered || !isCrmIframeMode()) return;
  isRegistered = true;
  // Server time (seconds), NOT Date.now(): message.date is Telegram server
  // time, so a skewed device clock would otherwise mis-gate live vs backlog
  // (slow clock → backlog burst; fast clock → live DMs dropped).
  reporterStartedAt = getServerTime();

  addActionHandler('apiUpdate', (global, actions, update) => {
    // Best-effort: a throw here propagates into teactn's action drain
    // (try/finally with no catch) and wipes the queue, breaking other
    // handlers in the tick — so swallow everything.
    try {
      if (update['@type'] === 'newMessage') {
        const { chatId, message } = update;
        // 1:1 user DMs only (skip groups/channels and Saved Messages), and
        // only genuinely-live messages: backlog/history carries old dates
        // and is left to the worker sweep so opening the inbox doesn't burst
        // lead-creates. message.date is in seconds, same as reporterStartedAt.
        if (
          chatId
          && isUserId(chatId)
          && chatId !== global.currentUserId
          && (!message.date || message.date >= reporterStartedAt)
        ) {
          const peer = global.users.byId[chatId];
          postToCrm({
            type: 'tg:newMessage',
            payload: {
              accountId: getCrmAccountId(),
              peerId: chatId,
              isOutgoing: Boolean(message.isOutgoing),
              username: peer ? getMainUsername(peer) : undefined,
              firstName: peer?.firstName,
              lastName: peer?.lastName,
              phone: peer?.phoneNumber,
            },
          });
        }
      }

      // Self-profile becomes available shortly after connect; cheap retry on
      // each update until reported once.
      maybeReportSelfProfile();
    } catch {
      // never disrupt the update pipeline
    }
  });
}
